import React, { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import appointmentService from "../services/appointmentService";
import fileService from "../services/fileService";
import Toast from "./Toast";
import { updateAppointmentData } from "../redux/slices/appointmentSlice";
import "../styles/ConsultationPage.css";

const ConsultationPage = () => {
  const { appointmentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authState = useSelector((state) => state.auth);
  const isAdmin = useSelector((state) => state.admin.isAuthenticated);
  const doctorId = authState.doctor?.doctorId;

  const [appointment, setAppointment] = useState(
    location.state?.appointment || null
  );
  const [remarks, setRemarks] = useState(appointment?.remarks || "");
  const [fees, setFees] = useState(appointment?.fees || "");
  const [isLoading, setIsLoading] = useState(!appointment);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [appointmentStatus, setAppointmentStatus] = useState(appointment?.status || "scheduled");
  
  // Local file queue (files to be uploaded with consultation)
  const [pendingFiles, setPendingFiles] = useState([]);
  
  // Already uploaded files
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);

  useEffect(() => {
    const fetchAppointmentDetails = async () => {
      try {
        setIsLoading(true);
        const data = await appointmentService.getAppointmentById(appointmentId, doctorId, isAdmin);
        
        // Authorization check: verify appointment belongs to logged-in doctor or user is admin
        if (!isAdmin && data.doctor_id !== parseInt(doctorId)) {
          setError("Unauthorized: You don't have access to this appointment");
          setTimeout(() => {
            navigate("/dashboard");
          }, 2000);
          return;
        }
        
        setAppointment(data);
        setRemarks(data.remarks || "");
        setFees(data.fees || "");
        // Set status based on appointment status
        setAppointmentStatus(data.status || "scheduled");
        setError(null);
      } catch (err) {
        setError("Failed to load appointment details");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    // Always fetch fresh data from API on mount/when appointmentId changes
    fetchAppointmentDetails();
  }, [appointmentId, doctorId, isAdmin, navigate]);

  // Fetch already uploaded files
  useEffect(() => {
    const fetchUploadedFiles = async () => {
      try {
        setIsLoadingFiles(true);
        const files = await fileService.getFilesByAppointment(appointmentId);
        setUploadedFiles(files || []);
      } catch (err) {
        console.error("Failed to load files:", err);
      } finally {
        setIsLoadingFiles(false);
      }
    };

    if (appointmentId) {
      fetchUploadedFiles();
    }
  }, [appointmentId]);

  // Handle adding files to pending queue
  const handleAddFile = async (event) => {
    const file = event.target.files[0];

    if (!file) {
      return;
    }

    // Check file limit (max 3 files including already uploaded)
    const totalFiles = pendingFiles.length + uploadedFiles.length;
    if (totalFiles >= 3) {
      setError("Maximum 3 files allowed per consultation");
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      setError("File size exceeds 50MB limit");
      return;
    }

    // Add to pending files queue (not uploaded yet)
    const newPendingFile = {
      id: Date.now(), // Temporary local ID
      file: file,
      name: file.name,
      size: file.size,
    };

    setPendingFiles([...pendingFiles, newPendingFile]);
    event.target.value = ""; // Reset file input
  };

  // Remove pending file from queue
  const handleRemovePendingFile = (fileId) => {
    setPendingFiles(pendingFiles.filter((f) => f.id !== fileId));
  };

  // Delete already uploaded file
  const handleDeleteUploadedFile = async (fileId) => {
    try {
      if (window.confirm("Are you sure you want to delete this file?")) {
        await fileService.deleteFile(fileId);
        setUploadedFiles(uploadedFiles.filter((f) => f.file_id !== fileId));
        setSuccessMessage("File deleted successfully!");

        setTimeout(() => {
          setSuccessMessage(null);
        }, 3000);
      }
    } catch (err) {
      setError("Failed to delete file");
      console.error(err);
    }
  };

  // View uploaded file in new tab
  const handleViewFile = async (fileId, fileName) => {
    try {
      const url = await fileService.getDownloadUrl(fileId);
      window.open(url, "_blank");
    } catch (err) {
      setError("Failed to open file");
      console.error(err);
    }
  };

  // Save consultation with remarks and upload pending files
  const handleSaveConsultation = async () => {
    try {
      setIsSaving(true);
      setError(null);
      
      // Update remarks and status
      const updatedData = {
        appointmentDate: appointment.appointment_date,
        appointmentTime: appointment.appointment_time,
        roomId: appointment.room_id,
        status: appointmentStatus,
        remarks: remarks,
        fees: fees ? parseFloat(fees) : null,
      };

      const result = await appointmentService.updateAppointment(
        appointmentId,
        updatedData
      );

      dispatch(updateAppointmentData({ id: appointmentId, appointmentData: updatedData }));
      setAppointment(result);
      // Update status to match the saved status
      setAppointmentStatus(result.status || "scheduled");

      // Upload pending files
      if (pendingFiles.length > 0) {
        const uploadedFilesList = [];
        
        for (const pendingFile of pendingFiles) {
          try {
            const fileResult = await fileService.uploadFile(
              appointment.patient_id,
              appointmentId,
              pendingFile.file,
              doctorId
            );
            uploadedFilesList.push(fileResult.file);
          } catch (fileErr) {
            console.error(`Failed to upload file ${pendingFile.name}:`, fileErr);
          }
        }

        // Add newly uploaded files to the uploaded files list
        if (uploadedFilesList.length > 0) {
          setUploadedFiles([...uploadedFiles, ...uploadedFilesList]);
        }

        setSuccessMessage(
          `Consultation saved! ${uploadedFilesList.length}/${pendingFiles.length} files uploaded.`
        );
        setPendingFiles([]); // Clear pending files
      } else {
        setSuccessMessage("Consultation remarks saved successfully!");
      }
      
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      setError("Failed to save consultation");
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <div className="consultation-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading appointment details...</p>
        </div>
      </div>
    );
  }

  if (!appointment) {
    return (
      <div className="consultation-page">
        <div className="error-container">
          <p className="error-message">Appointment not found</p>
          <button className="back-button" onClick={handleBack}>
            ← Return
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="consultation-page">
      <Toast
        message={successMessage}
        type="success"
        onClose={() => setSuccessMessage(null)}
      />

      <div className="consultation-header">
        <button className="back-button" onClick={handleBack}>
          ← Return
        </button>
        <h1>Consultation</h1>
      </div>

      <div className="consultation-container">
        {error && <div className="error-banner">{error}</div>}

        <div className="consultation-grid">
          {/* Patient Information Card */}
          <div 
            className="info-card patient-card"
            onClick={() => navigate(`/patient/${appointment.patient_id}`)}
            style={{ cursor: "pointer", transition: "box-shadow 0.2s" }}
            onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 4px 12px rgba(25, 118, 210, 0.3)")}
            onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "")}
          >
            <div className="card-header">
              <h2>👤 Patient Information</h2>
            </div>
            <div className="card-content">
              <div className="info-row">
                <span className="info-label">Name:</span>
                <span className="info-value">
                  {appointment.patient_first_name} {appointment.patient_last_name}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Age:</span>
                <span className="info-value">{appointment.patient_age || "—"}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Gender:</span>
                <span className="info-value">{appointment.patient_gender || "—"}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Contact:</span>
                <span className="info-value">{appointment.patient_phone || "—"}</span>
              </div>
            </div>
          </div>

          {/* Appointment Details Card */}
          <div className="info-card appointment-card">
            <div className="card-header">
              <h2>📅 Appointment Details</h2>
            </div>
            <div className="card-content">
              <div className="info-row">
                <span className="info-label">Date:</span>
                <span className="info-value">
                  {new Date(appointment.appointment_date).toLocaleDateString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Time:</span>
                <span className="info-value">{appointment.appointment_time}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Duration:</span>
                <span className="info-value">{appointment.appointment_duration} minutes</span>
              </div>
              <div className="info-row">
                <span className="info-label">Status:</span>
                <span className={`status-badge ${appointment.status}`}>
                  {appointment.status}
                </span>
              </div>
              {appointment.room_number && (
                <div className="info-row">
                  <span className="info-label">Room:</span>
                  <span className="info-value">{appointment.room_number}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Consultation Remarks Form */}
        <div className="consultation-form-card">
          <div className="card-header">
            <h2>📝 Consultation Remarks</h2>
            <p className="form-subtitle">Add your medical findings and recommendations</p>
          </div>
          <div className="form-content">
            <div className="form-group">
              <label htmlFor="remarks" className="form-label">
                Clinical Notes & Recommendations
              </label>
              <textarea
                id="remarks"
                className="remarks-textarea"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Enter your consultation notes, findings, diagnosis, and recommendations..."
                rows="8"
              />
              <p className="char-count">
                {remarks.length} characters
              </p>
            </div>

            <div className="form-group">
              <label htmlFor="fees" className="form-label">
                Consultation Fees
              </label>
              <input
                id="fees"
                type="number"
                className="fees-input"
                value={fees}
                onChange={(e) => setFees(e.target.value)}
                placeholder="Enter consultation fees amount"
                step="1"
                min="0"
              />
            </div>

            {/* File Management Section - Inside Consultation Form */}
            <div className="file-section">
              <div className="file-section-header">
                <h3>📎 Medical Files & Attachments</h3>
                <p className="file-section-subtitle">Add files to include with your consultation (max 3 files)</p>
              </div>

              {/* File Upload Area */}
              <div className="file-upload-area">
                <label className="file-upload-label" htmlFor="file-input">
                  <input
                    id="file-input"
                    type="file"
                    onChange={handleAddFile}
                    disabled={isSaving || (pendingFiles.length + uploadedFiles.length >= 3)}
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                    style={{ display: "none" }}
                  />
                  <div className="upload-icon">📤</div>
                  <p className="upload-text">
                    Click to select files or drag and drop
                  </p>
                  <p className="upload-subtext">
                    PDF, Images, Word, Excel (Max 50MB each) • {pendingFiles.length + uploadedFiles.length}/3 files
                  </p>
                </label>
              </div>

              {/* Pending Files (to be uploaded) */}
              {pendingFiles.length > 0 && (
                <div className="pending-files-section">
                  <p className="files-title">
                    📋 Files to Upload ({pendingFiles.length})
                  </p>
                  <div className="files-container">
                    {pendingFiles.map((file) => (
                      <div key={file.id} className="file-item pending-file">
                        <div className="file-info">
                          <span className="file-icon">📎</span>
                          <div className="file-details">
                            <p className="file-name">{file.name}</p>
                            <p className="file-meta">
                              {(file.size / 1024).toFixed(2)} KB • Not yet uploaded
                            </p>
                          </div>
                        </div>
                        <div className="file-actions">
                          <button
                            className="remove-btn"
                            onClick={() => handleRemovePendingFile(file.id)}
                            title="Remove this file"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="file-notice">
                    💡 Click the ✕ button to remove files before saving
                  </p>
                </div>
              )}

              {/* Already Uploaded Files */}
              {isLoadingFiles ? (
                <div className="loading-files">Loading files...</div>
              ) : uploadedFiles && uploadedFiles.length > 0 ? (
                <div className="uploaded-files-section">
                  <p className="files-title">
                    ✅ Uploaded Files ({uploadedFiles.length})
                  </p>
                  <div className="files-container">
                    {uploadedFiles.map((file) => (
                      <div key={file.file_id} className="file-item uploaded-file">
                        <div className="file-info">
                          <span className="file-icon">📄</span>
                          <div className="file-details">
                            <p className="file-name">{file.originalFileName || file.original_file_name}</p>
                            <p className="file-meta">
                              {file.fileSize ? (file.fileSize / 1024).toFixed(2) : file.file_size ? (file.file_size / 1024).toFixed(2) : 0} KB •{" "}
                              {file.uploadedAt ? new Date(file.uploadedAt).toLocaleDateString() : file.uploaded_at ? new Date(file.uploaded_at).toLocaleDateString() : "Unknown date"}
                            </p>
                          </div>
                        </div>
                        <div className="file-actions">
                          <button
                            className="view-btn"
                            onClick={() =>
                              handleViewFile(file.fileId || file.file_id, file.originalFileName || file.original_file_name)
                            }
                            title="View file"
                          >
                            👁️
                          </button>
                          <button
                            className="delete-btn"
                            onClick={() => handleDeleteUploadedFile(file.file_id)}
                            title="Delete file"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                pendingFiles.length === 0 && (
                  <div className="no-files-inline">
                    <p>No files attached yet</p>
                  </div>
                )
              )}
            </div>

            {/* Status Selection Checkboxes */}
            <div className="form-group checkbox-group">
              <label className="checkbox-group-label">Set Appointment Status:</label>
              <div className="checkbox-options">
                <label className="checkbox-label">
                  <input
                    type="radio"
                    name="appointment-status"
                    value="scheduled"
                    checked={appointmentStatus === "scheduled"}
                    onChange={(e) => setAppointmentStatus(e.target.value)}
                    className="checkbox-input"
                    disabled={appointment.status !== "scheduled"}
                    title={appointment.status !== "scheduled" ? "Cannot revert to Scheduled once status has been changed" : ""}
                  />
                  <span className="checkbox-text" style={{opacity: appointment.status !== "scheduled" ? 0.5 : 1}}>Scheduled</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="radio"
                    name="appointment-status"
                    value="completed"
                    checked={appointmentStatus === "completed"}
                    onChange={(e) => setAppointmentStatus(e.target.value)}
                    className="checkbox-input"
                  />
                  <span className="checkbox-text">Completed</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="radio"
                    name="appointment-status"
                    value="cancelled"
                    checked={appointmentStatus === "cancelled"}
                    onChange={(e) => setAppointmentStatus(e.target.value)}
                    className="checkbox-input"
                  />
                  <span className="checkbox-text">Cancelled</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="radio"
                    name="appointment-status"
                    value="no_show"
                    checked={appointmentStatus === "no_show"}
                    onChange={(e) => setAppointmentStatus(e.target.value)}
                    className="checkbox-input"
                  />
                  <span className="checkbox-text">No Show</span>
                </label>
              </div>
            </div>

            <div className="form-actions">
              <button
                className="cancel-button"
                onClick={handleBack}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                className="save-button"
                onClick={handleSaveConsultation}
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save Consultation"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultationPage;
